const FONT_FAMILY = 'Inter, system-ui, -apple-system, sans-serif';
const FONT_SIZE = 28;
const FONT_WEIGHT = '900';
const ANGLE = -30;
const OPACITY = 0.2;

// Padding around the text within each tile (per design specs)
const PADDING_X = 0;
const PADDING_Y = 40;

// Minimum tile dimensions to prevent overly small patterns
const MIN_WIDTH = 120;
const MIN_HEIGHT = 60;

/**
 * Measures text width using the Canvas API.
 * Falls back to character-based estimation if Canvas is unavailable.
 */
const measureTextWidth = (text: string): number => {
  if (typeof document === 'undefined') {
    // SSR fallback: rough estimate based on character count
    return text.length * FONT_SIZE * 0.6;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return text.length * FONT_SIZE * 0.6;
  }

  ctx.font = `${FONT_WEIGHT} ${FONT_SIZE}px ${FONT_FAMILY}`;
  return ctx.measureText(text).width;
};

/**
 * Escapes special characters for safe inclusion in XML/SVG content.
 */
const escapeXml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

export interface WatermarkSvgResult {
  dataUri: string;
  width: number;
  height: number;
}

/**
 * Generates a data URI for an SVG watermark pattern tile.
 *
 * The generated SVG contains a single rotated text element that will be
 * repeated using CSS `background-repeat: repeat` to create a tiled pattern
 * across the entire viewport.
 */
export const generateWatermarkSvg = (text: string): WatermarkSvgResult => {
  const uppercaseText = text.toUpperCase();
  const escapedText = escapeXml(uppercaseText);

  const textWidth = measureTextWidth(uppercaseText);

  // Tile width = text width + padding (text starts at left edge)
  const width = Math.max(MIN_WIDTH, Math.ceil(textWidth + PADDING_X));
  // Height needs to accommodate rotated text vertical extent
  const height = Math.max(MIN_HEIGHT, Math.ceil(textWidth * 0.5 + FONT_SIZE + PADDING_Y));

  // Text positioned at left edge, vertically centered
  const textX = 0;
  const textY = height / 2;

  // Rotate around text center (halfway along text width)
  const pivotX = textWidth / 2;
  const pivotY = textY;

  const svg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  <text
    x="${textX}"
    y="${textY}"
    text-anchor="start"
    dominant-baseline="middle"
    transform="rotate(${ANGLE} ${pivotX} ${pivotY})"
    fill="white"
    opacity="${OPACITY}"
    font-size="${FONT_SIZE}"
    font-family="${FONT_FAMILY}"
    font-weight="${FONT_WEIGHT}"
    style="text-shadow: 0 0 4px rgba(0, 0, 0, 0.60)">
    ${escapedText}
  </text>
</svg>`;

  return {
    dataUri: `data:image/svg+xml;base64,${btoa(svg)}`,
    width,
    height,
  };
};
